import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BatteryFullIcon, CheckIcon, DropletsIcon, EyeIcon, RefreshCwIcon } from 'lucide-react';
import { ObjectionId, objectionChoices, RECOVERY_ID } from '../data/objections';
import { lenses, offers } from '../data/product';
import { CtaButton } from './CtaButton';
import { LifetimeSeal } from './LifetimeSeal';
import { useTrackOnView } from '../hooks/useTrackOnView';
import { trackCustom, packEvents } from '../utils/tracking';
import { requestOffer, PackId } from '../utils/offerBus';
interface ObjectionRecoveryProps {
  /** The objection the visitor picked. Nothing renders until they pick one. */
  active: ObjectionId | null;
  /** True once the visitor has entered the price path at least once. */
  priceUnlocked: boolean;
  /** Reopens the six-option selector. */
  onChooseAnother: () => void;
}
const headlineClass = 'grit-headline text-[clamp(2.25rem,8vw,4.5rem)] font-extrabold uppercase leading-[0.92] text-offwhite';
const subClass = 'mt-5 max-w-2xl text-[15px] leading-relaxed text-steel-300 sm:text-lg';
const statementClass = 'mt-6 border-l-4 border-grit-500 pl-4 font-display text-2xl font-bold uppercase leading-tight text-offwhite sm:text-3xl';
function Bullets({
  items
}: {
  items: string[];
}) {
  return <ul className="mt-6 grid gap-2.5" data-id="element-370" data-mp="29ydb3cvrjpyz">
      {items.map(line => <li key={line} className="flex items-start gap-2.5 text-[15px] font-medium text-offwhite" data-id="element-371" data-mp="2xmmr1y6apji7">
          <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-372" data-mp="32kfyeujc4jk2" />
          {line}
        </li>)}
    </ul>;
}

/**
 * The compact close every funnel ends on: the three consumer packs, with the
 * one that answers this objection marked as recommended. Picking one preselects
 * it in the main offer section and drops the visitor there.
 */
function PackClose({
  recommended,
  cta
}: {
  recommended: PackId;
  cta: string;
}) {
  return <div className="mt-10 border-t border-white/10 pt-8" data-id="element-373" data-mp="3v7xv5ov9w5x9">
      <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-grit-500" data-id="element-374" data-mp="3v5bl9ikwtebv">Recommended for you</p>
      <ul className="mt-4 grid gap-3 sm:grid-cols-3" data-id="element-375" data-mp="3m6zlapy5l46r">
        {offers.map(offer => {
        const isPick = offer.id === recommended;
        return <li key={offer.id} data-id="element-376" data-mp="3h7el6c78ypsg">
              <a href="#offer" data-buy-intent="true" onClick={() => {
            requestOffer(offer.id);
            trackCustom(packEvents[offer.id].addToCart);
            trackCustom('mini_funnel_cta_clicked');
          }} className={`flex h-full flex-col border p-4 transition-[border-color,background-color] duration-150 ease-grit focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500 ${isPick ? 'border-grit-500 bg-grit-500/10 hover:bg-grit-500/15' : 'border-white/15 bg-ink-900 hover:border-white/35'}`} data-id="element-377" data-mp="3c8tm4g3qec5l">
                {offer.badge ? <span className="mb-2 self-start bg-grit-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-ink-950" data-id="element-378" data-mp="18z8gcijjmvht">
                    {offer.badge}
                  </span> : <span className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-steel-500" data-id="element-379" data-mp="1i0g2k28sc5vj">
                    {offer.name}
                  </span>}
                <span className="font-display text-2xl font-bold uppercase leading-none text-offwhite" data-id="element-380" data-mp="qav2fqy5ejbi">
                  {offer.units}
                </span>
                <span className="mt-2 font-display text-3xl font-extrabold leading-none text-offwhite" data-id="element-381" data-mp="z23r29ueijws">
                  {offer.price}
                </span>
                {offer.perUnit && <span className="mt-1 text-[12px] font-semibold text-steel-400" data-id="element-382" data-mp="6lt005yft5p4">{offer.perUnit}</span>}
              </a>
            </li>;
      })}
      </ul>

      <CtaButton offer={recommended} className="mt-6 w-full sm:w-auto" onClick={() => trackCustom('mini_funnel_cta_clicked')} data-id="element-383" data-mp="3jk91a6ywrotx">
        {cta}
      </CtaButton>
    </div>;
}
function BatteryFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-384" data-mp="3okn9pq4f90sh">
        Your shift doesn&apos;t end
        <br data-id="element-385" data-mp="675yglchiht9" />
        <span className="text-grit-500" data-id="element-386" data-mp="66w5qq021wjv">when one battery does.</span>
      </h2>
      <p className={subClass} data-id="element-387" data-mp="18umo52mpx8e">
        We are not going to pretend one X1 lasts an entire workday. One unit runs up to 14 hours on low and about 5
        on high, so if you burn high light all day, you rotate. That is exactly what the Full-Shift Kit is for.
      </p>
      <p className={statementClass} data-id="element-388" data-mp="b4padelpmii1">
        One for the job.
        <br data-id="element-389" data-mp="2hy9nqj9atobo" />
        Three for the shift.
      </p>

      <div className="mt-8 grid gap-3 sm:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1.2fr] sm:items-center" data-id="element-390" data-mp="2cy53qj96mbgp">
        {['X1 #1', 'X1 #2', 'X1 #3'].map((unit, index) => <React.Fragment key={unit} data-id="element-391" data-mp="sgw83ulw30dm" data-mpi={index}>
            <div className="flex items-center gap-3 border border-white/12 bg-ink-900 px-4 py-4" data-id="element-392" data-mp="xexr6fjbezp3">
              <BatteryFullIcon className="h-7 w-7 shrink-0 text-grit-500" aria-hidden="true" data-id="element-393" data-mp="iqkrp8l0qypu" />
              <span className="font-display text-xl font-bold uppercase text-offwhite" data-id="element-394" data-mp="npe5z384qtr2">{unit}</span>
            </div>
            <span aria-hidden="true" className="hidden text-center font-display text-2xl font-extrabold text-steel-500 sm:block" data-id="element-395" data-mp="9p3dxwv3394b">
              {index < 2 ? '+' : '='}
            </span>
          </React.Fragment>)}
        <div className="bg-grit-500 px-4 py-4 text-center font-display text-xl font-extrabold uppercase leading-tight text-ink-950" data-id="element-396" data-mp="3vz5zuflwcgoh">
          Up to 10 hours of combined light*
        </div>
      </div>

      <p className="mt-6 font-display text-xl font-bold uppercase tracking-wide text-steel-200" data-id="element-397" data-mp="1u3na896ohycn">
        Use one. Swap when needed. Keep working.
      </p>
      <p className="mt-3 text-[11px] leading-relaxed text-steel-500" data-id="element-398" data-mp="1xo5ilk7llv9b">
        *Up to approximately 10 hours of combined runtime when three X1 units are used sequentially. Runtime varies
        depending on light mode and usage.
      </p>
    </>;
}
function VisibilityFunnel() {
  const rows = [{
    label: 'Shop light overhead',
    body: 'The light stays where the lamp is, not where your head goes.'
  }, {
    label: 'Handheld flashlight',
    body: 'One hand is gone the moment you pick it up.'
  }, {
    label: 'GritVue X1',
    body: '800 lumens travel with your line of sight while both hands stay on the job.',
    highlight: true
  }];
  return <>
      <h2 className={headlineClass} data-id="element-399" data-mp="21pimhor4rodl">
        The light should be
        <br data-id="element-400" data-mp="21n4trmjxao97" />
        <span className="text-grit-500" data-id="element-401" data-mp="2z43aa7ogwb8z">where your eyes are.</span>
      </h2>
      <p className={subClass} data-id="element-402" data-mp="2z6fy31gwpfty">
        800 lumens mounted in the frame, pointed at whatever you are looking at. Under a hood, inside a cabinet,
        above a ceiling, inside machinery, in a crawlspace you can barely turn around in.
      </p>

      <ul className="mt-8 grid gap-3 lg:grid-cols-3" data-id="element-403" data-mp="36n47yccl7aof">
        {rows.map(row => <li key={row.label} className={`border p-5 ${row.highlight ? 'border-grit-500 bg-grit-500/10' : 'border-white/12 bg-ink-900'}`} data-id="element-404" data-mp="3bmfr1s0fes96">
            <EyeIcon className={`h-7 w-7 ${row.highlight ? 'text-grit-500' : 'text-steel-500'}`} aria-hidden="true" data-id="element-405" data-mp="3gjpex3862vuw" />
            <p className="mt-3 font-display text-xl font-bold uppercase leading-tight text-offwhite" data-id="element-406" data-mp="3plfcyrwk0t1n">{row.label}</p>
            <p className="mt-1.5 text-[14px] leading-relaxed text-steel-300" data-id="element-407" data-mp="3ue8i3gvxq3e9">{row.body}</p>
          </li>)}
      </ul>

      <p className={statementClass} data-id="element-408" data-mp="868mzke3mszv">
        Both hands on the job.
        <br data-id="element-409" data-mp="8838p0x72p50" />
        Light on the task.
      </p>
    </>;
}
function FoggingFunnel() {
  const included = [{
    name: 'GritVue anti-fog',
    tag: 'Included free'
  }, {
    name: 'GritVue hard case',
    tag: 'Included free'
  }, {
    name: 'Lifetime Limited Warranty',
    tag: 'Included'
  }];
  return <>
      <h2 className={headlineClass} data-id="element-410" data-mp="tjb85u2xgadr">
        Sweat. Heat. Humidity.
        <br data-id="element-411" data-mp="17foq14bdidsr" />
        <span className="text-grit-500" data-id="element-412" data-mp="17ggc9oj2yz6d">Real work happens in the real world.</span>
      </h2>
      <p className={subClass} data-id="element-413" data-mp="12iermz60arss">
        Every pair of work glasses fogs when the temperature swings. That is why your X1 ships with GritVue anti-fog
        in the box, not as something we sell you later. Eyewear should arrive ready for the conditions you work in.
      </p>

      <div className="mt-8 grid gap-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-center" data-id="element-414" data-mp="oidote66laao">
        {/* Placeholder until the branded anti-fog render is supplied. */}
        <div className="flex aspect-[4/3] items-center justify-center border border-white/12 bg-ink-900" data-id="element-415" data-mp="jpkpj5theu5z">
          <DropletsIcon className="h-16 w-16 text-grit-500" aria-hidden="true" data-id="element-416" data-mp="3n844j6n5g3iu" />
        </div>
        <ul className="grid gap-3" data-id="element-417" data-mp="22q3evblwr2rl">
          {included.map(item => <li key={item.name} className="flex items-center justify-between gap-4 border border-white/12 bg-ink-900 px-5 py-4" data-id="element-418" data-mp="22osbhcwdjx10">
              <span className="font-display text-xl font-bold uppercase leading-tight text-offwhite" data-id="element-419" data-mp="1tqgf0u8tu50n">
                {item.name}
              </span>
              <span className="shrink-0 bg-grit-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-ink-950" data-id="element-420" data-mp="2me2qsps7kfyl">
                {item.tag}
              </span>
            </li>)}
        </ul>
      </div>

      <p className={statementClass} data-id="element-421" data-mp="2cgo9knygnn58">Your X1 comes work-ready.</p>
    </>;
}
function LensFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-422" data-mp="2hdoeuwnhklva">
        One frame.
        <br data-id="element-423" data-mp="110zndyt16s5h" />
        <span className="text-grit-500" data-id="element-424" data-mp="1106o1iwej4xd">Three ways to work.</span>
      </h2>
      <p className={subClass} data-id="element-425" data-mp="15zt37xvsiv8t">
        Every X1 arrives with all three lenses. They pop out tool-free, so you change the tint instead of changing
        the glasses, and any optician can fit your prescription into the clear lens.
      </p>

      <ul className="mt-8 grid gap-3 sm:grid-cols-3" data-id="element-426" data-mp="3l5lvcj0vjuu4">
        {lenses.map(lens => <li key={lens.name} className="border border-white/12 bg-ink-900 p-5" data-id="element-427" data-mp="36iix2a4q9i5q">
            <span className="block h-10 w-10 rounded-full border border-white/20" style={{
          backgroundColor: lens.swatch
        }} aria-hidden="true" data-id="element-428" data-mp="3bgaoz45dh49x" />
            <p className="mt-4 font-display text-2xl font-bold uppercase leading-none text-offwhite" data-id="element-429" data-mp="31ied2a4m25ar">{lens.name}</p>
            <p className="mt-2 text-[14px] leading-relaxed text-steel-300" data-id="element-430" data-mp="2njol18wnevt9">{lens.use}</p>
          </li>)}
      </ul>

      <p className={statementClass} data-id="element-431" data-mp="2sihxcy078gru">
        Replace the lens.
        <br data-id="element-432" data-mp="lr0eapkspq8q" />
        Not the system.
      </p>
      <p className="mt-6 font-display text-xl font-bold uppercase tracking-wide text-steel-200" data-id="element-433" data-mp="lsbdsd77kbu1">
        1 X1 = 3 lenses · Full-Shift Kit = 9 lenses · Max Value = 15 lenses
      </p>
    </>;
}
function DurabilityFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-434" data-mp="26a2rhok8p5yl">
        Truck. Toolbag.
        <br data-id="element-435" data-mp="267g9euzq3824" />
        <span className="text-grit-500" data-id="element-436" data-mp="267q24qc5jtbi">Jobsite. Repeat.</span>
      </h2>
      <p className={subClass} data-id="element-437" data-mp="2kzru7o7zdi0z">
        The X1 lives in a hard case in your bag and gets pulled out with greasy gloves ten times a day. If it breaks,
        cracks or a lens gets damaged, we replace it free for as long as you own it under our Lifetime Limited
        Warranty. One email is all it takes.
      </p>

      <div className="mt-8 flex flex-col items-start gap-6 border border-grit-500/50 bg-ink-900 p-6 sm:flex-row sm:items-center sm:p-8" data-id="element-438" data-mp="2p2er43jidt51">
        <LifetimeSeal className="h-24 w-24 shrink-0" data-id="element-439" data-mp="2yxoqskmfpofn" />
        <div data-id="element-440" data-mp="2yw5baw8lrhex">
          <p className="font-display text-2xl font-bold uppercase leading-tight text-offwhite sm:text-3xl" data-id="element-441" data-mp="19ja8oj5x8zt4">
            Backed by our Lifetime Limited Warranty.
          </p>
          <p className="mt-2 text-[15px] leading-relaxed text-steel-300" data-id="element-442" data-mp="14m1nsate2ho6">
            Included on every multi-pack. Single units are covered for one year.
          </p>
          <Link to="/legal/guarantee" className="mt-3 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-grit-500 underline-offset-4 transition-colors duration-150 ease-grit hover:text-grit-400 hover:underline" data-id="element-443" data-mp="1yazdc3s846za">
            View Lifetime Limited Warranty terms
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-444" data-mp="2d549uqpybrd0" />
          </Link>
        </div>
      </div>

      <p className={statementClass} data-id="element-445" data-mp="2h6fqw7urf49l">
        We built the X1 to be used.
        <br data-id="element-446" data-mp="2m02544gkng7i" />
        Not babied.
      </p>
    </>;
}
function PriceFunnel({
  priceUnlocked
}: {
  priceUnlocked: boolean;
}) {
  const splitRef = useTrackOnView<HTMLDivElement>('price_split_offer_viewed');
  const rescueRef = useTrackOnView<HTMLDivElement>('rescue_20_viewed', priceUnlocked);
  return <>
      <h2 className={headlineClass} data-id="element-447" data-mp="30x1cbr7ih71g">Don&apos;t want to spend that much?</h2>
      <p className="mt-5 font-display text-2xl font-bold uppercase tracking-wide text-grit-500 sm:text-3xl" data-id="element-448" data-mp="pa7gxv8pm5bn">
        You don&apos;t have to keep all three.
      </p>
      <p className="mt-6 border-l-4 border-grit-500 pl-4 font-display text-[clamp(1.75rem,5vw,3rem)] font-extrabold uppercase leading-tight text-offwhite" data-id="element-449" data-mp="faqd7jd0q1ib">
        Keep one.
        <br data-id="element-450" data-mp="aejf2cqv5uax" />
        Split the rest with your crew.
      </p>

      <div ref={splitRef} className="mt-10 grid gap-6 lg:grid-cols-2" data-id="element-451" data-mp="17ze5hyw6pr5d">
        <div className="border border-white/12 bg-ink-900 p-6 sm:p-8" data-id="element-452" data-mp="130kqu355vu3m">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-453" data-mp="ya49i7y7zobz">Full-Shift Kit</p>
          <p className="mt-1 font-display text-2xl font-bold uppercase text-offwhite" data-id="element-454" data-mp="tbav8db3ztar">3 X1</p>
          <p className="mt-2 font-display text-5xl font-extrabold leading-none text-offwhite" data-id="element-455" data-mp="1qq5a6n588tdf">$74.95</p>
          <ul className="mt-6 grid gap-2 border-t border-white/10 pt-5" data-id="element-456" data-mp="1lzynisu9zixt">
            {['You: 1 X1', 'Coworker: 1 X1', 'Coworker: 1 X1'].map(line => <li key={line} className="flex items-center gap-2.5 text-[15px] font-semibold text-offwhite" data-id="element-457" data-mp="16gdub1izuzwz">
                <CheckIcon className="h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-458" data-mp="1bep45c9ahaf7" />
                {line}
              </li>)}
          </ul>
          <p className="mt-4 bg-grit-500 px-4 py-3 font-display text-2xl font-extrabold uppercase leading-none text-ink-950 sm:text-3xl" data-id="element-459" data-mp="11jngkkunwgeq">
            $24.98 each
          </p>
        </div>

        <div className="border border-white/12 bg-ink-900 p-6 sm:p-8" data-id="element-460" data-mp="1u7jbpl4l209e">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-461" data-mp="1fidcvy9nqc21">
            Your coworkers may want their own too
          </p>
          <dl className="mt-4 grid gap-2.5 text-[15px]" data-id="element-462" data-mp="1kfvo5vb5d3d7">
            <div className="flex items-baseline justify-between gap-4" data-id="element-463" data-mp="286iz6pgd093z">
              <dt className="text-steel-300" data-id="element-464" data-mp="2857tgk3csra8">Coworker #1</dt>
              <dd className="font-display text-2xl font-extrabold text-offwhite" data-id="element-465" data-mp="2cyj4yyj41w8h">$39.95</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4" data-id="element-466" data-mp="1h7jr3tzsmvo">
              <dt className="text-steel-300" data-id="element-467" data-mp="3so1xa15vvjtn">Coworker #2</dt>
              <dd className="font-display text-2xl font-extrabold text-offwhite" data-id="element-468" data-mp="b8lobq0swcvo">$39.95</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4 border-t border-white/10 pt-3" data-id="element-469" data-mp="69qwxzorpfzy">
              <dt className="font-semibold text-offwhite" data-id="element-470" data-mp="3ep26amuwesub">Total</dt>
              <dd className="font-display text-3xl font-extrabold text-grit-500" data-id="element-471" data-mp="3erept4gb1b9h">$79.90</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4" data-id="element-472" data-mp="39sk7s4q5vqqf">
              <dt className="text-steel-300" data-id="element-473" data-mp="3nrbhzg1wks9a">Pack cost</dt>
              <dd className="font-display text-2xl font-extrabold text-offwhite" data-id="element-474" data-mp="3jo5euha37h71">$74.95</dd>
            </div>
          </dl>
          <p className="mt-5 font-display text-2xl font-bold uppercase leading-tight text-offwhite" data-id="element-475" data-mp="12ygcvkgu3yja">
            Your pack could be fully covered.*
            <span className="mt-1 block text-grit-500" data-id="element-476" data-mp="3rsmwnatd3n14">Your own X1 could effectively cost you $0.*</span>
          </p>
          <p className="mt-3 text-[11px] leading-relaxed text-steel-500" data-id="element-477" data-mp="ltxeci4pxz8">
            *Only if two coworkers voluntarily reimburse you at that amount. GritVue does not guarantee resale,
            coworker participation, reimbursement or profit. Taxes and fees may vary.
          </p>
        </div>
      </div>

      <CtaButton offer="trio" className="mt-8 w-full sm:w-auto" onClick={() => {
      trackCustom('price_split_clicked');
      trackCustom('mini_funnel_cta_clicked');
    }} data-id="element-478" data-mp="392y7jyc6z0to">
        Get 3 X1 — $74.95
      </CtaButton>

      {priceUnlocked && <div ref={rescueRef} className="mt-12 border-2 border-grit-500 bg-ink-900" data-id="element-479" data-mp="3mu3cnfybwlbe">
          <p className="bg-grit-500 px-5 py-2.5 text-[11px] font-extrabold uppercase tracking-[0.22em] text-ink-950 sm:px-7" data-id="element-480" data-mp="2qbx8lab2n5a4">
            Still just want one?
          </p>
          <div className="grid gap-8 p-5 sm:p-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center" data-id="element-481" data-mp="2ub61kcyaez4m">
            <div data-id="element-482" data-mp="2mrnd3r69ydon">
              <p className="font-display text-xl font-bold uppercase tracking-wide text-steel-200" data-id="element-483" data-mp="2hun6nf5ij8x0">
                We would rather let you try GritVue than watch you leave.
              </p>
              <h3 className="grit-headline mt-3 text-[clamp(2rem,6vw,3.5rem)] font-extrabold uppercase leading-[0.95] text-offwhite" data-id="element-484" data-mp="2duwb6y51k4m5">
                Here&apos;s <span className="text-grit-500" data-id="element-485" data-mp="23vx95yes7784">20% off</span> your first single X1.
              </h3>
              <p className="mt-4 text-[15px] leading-relaxed text-steel-300 sm:text-base" data-id="element-486" data-mp="1z343977ktsug">
                One X1 only. Not valid on the Full-Shift Kit, the Max Value pack or the Business / Crew Pack, and it
                cannot be combined with bundle pricing or other discount codes. One use per customer.
              </p>
              <Bullets items={['1 × GritVue X1 illuminated frame', '3 interchangeable lenses: Clear, Amber, Smoke', 'Free hard case and anti-fog', 'One-year replacement coverage']} data-id="element-487" data-mp="1l3makgr5tvft" />
            </div>

            <div className="border border-white/12 bg-ink-950 p-5 sm:p-7" data-id="element-488" data-mp="38ypqgzxsiaa4">
              <p className="text-base text-steel-400 line-through" data-id="element-489" data-mp="2v119i36ufnac">$39.95</p>
              <p className="font-display text-[clamp(3rem,11vw,5rem)] font-extrabold leading-none text-offwhite" data-id="element-490" data-mp="2zz2q8hgsrain">
                $31.96
              </p>
              <p className="mt-3 inline-flex bg-grit-500 px-3 py-1.5 font-display text-xl font-extrabold uppercase leading-none text-ink-950" data-id="element-491" data-mp="2q27w0ark3wa6">
                20% off
              </p>
              <a href="#offer" data-buy-intent="true" onClick={() => {
            requestOffer('single');
            trackCustom('rescue_20_clicked');
            trackCustom('mini_funnel_cta_clicked');
            trackCustom(packEvents.single.addToCart);
          }} className="mt-6 flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 text-center font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] sm:text-2xl" data-id="element-492" data-mp="2c902gdjx0qpu">
                Try 1 X1 — $31.96
                <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-493" data-mp="1srdw9bfuwg9q" />
              </a>
              <p className="mt-3 text-center text-[13px] text-steel-400" data-id="element-494" data-mp="26mpu3rpb6muv">Single X1 only. No discount stacking.</p>
            </div>
          </div>
        </div>}
    </>;
}
const closingCta: Record<ObjectionId, string> = {
  battery: 'Get the Full-Shift Kit — $74.95',
  visibility: 'Get the Full-Shift Kit — $74.95',
  fogging: 'Get your GritVue X1',
  lenses: 'Choose your X1 kit',
  durability: 'Try GritVue X1',
  price: 'Get 3 X1 — $74.95'
};

/**
 * One dynamic recovery area, hidden until the visitor tells us what is holding
 * them back. Only the matching funnel renders, so the normal page stays clean.
 */
export function ObjectionRecovery({
  active,
  priceUnlocked,
  onChooseAnother
}: ObjectionRecoveryProps) {
  const choice = active ? objectionChoices.find(item => item.id === active) : undefined;
  useEffect(() => {
    if (choice) trackCustom(choice.viewEvent);
  }, [choice]);
  return <AnimatePresence initial={false} data-id="element-495" data-mp="2e8fm9g9nqgj7">
      {choice && <motion.section id={RECOVERY_ID} key={choice.id} aria-labelledby="recovery-heading" className="scroll-mt-28 border-b border-white/10 bg-ink-950" initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 12
    }} transition={{
      duration: 0.24,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-496" data-mp="2e8z4yz74t8ya">
          <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20" data-id="element-497" data-mp="1vgn30dke49oo">
            <p className="inline-flex flex-wrap items-center gap-2 border border-grit-500/60 bg-grit-500/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-grit-500" data-id="element-498" data-mp="1qhts8swhmws7">
              You said this matters most to you:
              <span className="text-offwhite" data-id="element-499" data-mp="atnf9hcfpaqj">{choice.label}</span>
            </p>

            <div id="recovery-heading" className="mt-6" data-id="element-500" data-mp="3t73fcvz0f1xw">
              {choice.id === 'battery' && <BatteryFunnel data-id="element-501" data-mp="200n017fqpm3" />}
              {choice.id === 'visibility' && <VisibilityFunnel data-id="element-502" data-mp="pp3497y5qgg2" />}
              {choice.id === 'fogging' && <FoggingFunnel data-id="element-503" data-mp="pmp9l07e3691" />}
              {choice.id === 'lenses' && <LensFunnel data-id="element-504" data-mp="tpdj7a3xgh72" />}
              {choice.id === 'durability' && <DurabilityFunnel data-id="element-505" data-mp="fqm9dzw3lhoq" />}
              {choice.id === 'price' && <PriceFunnel priceUnlocked={priceUnlocked} data-id="element-506" data-mp="fq41u73zjh1i" />}
            </div>

            {choice.id !== 'price' && <PackClose recommended={choice.recommend} cta={closingCta[choice.id]} data-id="element-507" data-mp="krtetsarq4s8" />}

            <button type="button" onClick={onChooseAnother} className="mt-8 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-steel-400 transition-colors duration-150 ease-grit hover:text-grit-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-508" data-mp="30ixtu704t8x0">
              <RefreshCwIcon className="h-4 w-4" aria-hidden="true" data-id="element-509" data-mp="2ckhryegpn4mt" />
              Not your main concern? Choose another
            </button>
          </div>
        </motion.section>}
    </AnimatePresence>;
}